import {FC, useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {Page, PageTranslation} from '../core/_models'
import clsx from 'clsx'
import {useListView} from '../core/ListViewProvider'
import {DataLoading} from '../../../../../_dashboard/partials/components/loading/DataLoading'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {createOne, getAll, updateOne} from '../../../../services/common_requests'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {isNotEmpty} from '../../../../../_dashboard/helpers'
import TagsComponent from '../../../../../_dashboard/partials/widgets/tags/TagsComponent'
import {getBackofficeDefaultLanguage} from '../../../tours-management/configurations/models/personType'
type Props = {
  isPageLoading: boolean
  Page: Page
}

const optionalSeoText = (max: number) =>
  Yup.string()
    .transform((value) => (value == null ? '' : String(value)))
    .max(max, `Maximum ${max} symbols`)

const editPageSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().trim().max(120, 'Maximum 120 symbols'),
      metaTitle: optionalSeoText(200),
      metaDescription: optionalSeoText(500),
      metaKeywords: Yup.array().of(Yup.string()),
      openGraphTitle: optionalSeoText(200),
      openGraphDescription: optionalSeoText(500),
      twitterTitle: optionalSeoText(200),
      twitterDescription: optionalSeoText(500),
    })
  ),
  slug: Yup.string()
    .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Invalid slug format')
    .min(2, 'Minimum 2 symbols')
    .required('Slug is required'),
})

function extractLanguagesList(body: unknown): unknown[] {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (typeof body === 'object' && body !== null) {
    const o = body as {data?: unknown}
    if (Array.isArray(o.data)) return o.data
    const nested = (o.data as {data?: unknown[]} | undefined)?.data
    if (Array.isArray(nested)) return nested
  }
  return []
}

function normalizeLang(code: unknown): string {
  return String(code ?? '')
    .trim()
    .toLowerCase()
    .split('-')[0]
}

function emptyTranslation(language: string, seed?: Partial<PageTranslation>): PageTranslation {
  return {
    language,
    title: String(seed?.title || '').trim(),
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    openGraphType: seed?.openGraphType || 'website',
    openGraphTitle: '',
    openGraphDescription: '',
    openGraphImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
  }
}

function unwrapPagePayload(payload: any): any {
  if (!payload || typeof payload !== 'object') return payload
  if (Array.isArray(payload.translations) || payload.slug != null || payload._id) {
    return payload
  }
  if (payload.data) return unwrapPagePayload(payload.data)
  return payload
}

function normalizeTranslations(list?: PageTranslation[]): PageTranslation[] {
  if (!Array.isArray(list)) return []
  return list
    .filter((translation) => translation && typeof translation === 'object' && !Array.isArray(translation))
    .map((translation) => ({
      ...emptyTranslation(normalizeLang(translation.language) || 'fr', translation),
      ...translation,
      language: normalizeLang(translation.language) || 'fr',
      metaKeywords: Array.isArray(translation.metaKeywords) ? translation.metaKeywords : [],
    }))
}

function ensureTranslationForLanguage(
  translations: PageTranslation[],
  language: string
): PageTranslation[] {
  const lang = normalizeLang(language) || 'fr'
  const list = normalizeTranslations(translations)
  const seed = list.find((t) => t.language !== lang && String(t.title || '').trim())
  const existing = list.find((t) => t.language === lang)
  if (existing) {
    if (!String(existing.title || '').trim() && seed?.title) {
      return list.map((t) => (t.language === lang ? {...t, title: seed.title as string} : t))
    }
    return list
  }
  return [...list, emptyTranslation(lang, seed)]
}

function isSuccessCode(code: unknown): boolean {
  const n = Number(code)
  return Number.isFinite(n) && n >= 2000 && n < 3000
}

const PageEditModalForm: FC<Props> = ({Page}) => {
  const intl = useIntl()
  const LANGUAGE_NAMES: {[key: string]: string} = useMemo(
    () => ({
      en: intl.formatMessage({id: 'COMMON.ENGLISH_LANGUAGE'}),
      fr: intl.formatMessage({id: 'COMMON.FRENSH_LANGUAGE'}),
      es: intl.formatMessage({id: 'COMMON.ESPANGOL_LANGUAGE'}),
      de: intl.formatMessage({id: 'COMMON.GERMAN_LANGUAGE'}),
      ar: intl.formatMessage({id: 'COMMON.ARABIC_LANGUAGE'}),
    }),
    [intl]
  )
  const {itemIdForUpdate, setItemIdForUpdate} = useListView()
  const fallbackLanguages = useMemo(
    () => [
      {code: 'fr', name: LANGUAGE_NAMES.fr},
      {code: 'en', name: LANGUAGE_NAMES.en},
      {code: 'de', name: LANGUAGE_NAMES.de},
    ],
    [LANGUAGE_NAMES]
  )
  const [languages, setLanguages] = useState<{code: string; name: string}[]>(fallbackLanguages)
  const pageData = unwrapPagePayload(Page) || Page
  const initialLang =
    normalizeLang(pageData.translations?.find((t: PageTranslation) => t?.language)?.language) ||
    normalizeLang(getBackofficeDefaultLanguage()) ||
    'fr'
  const [selectedLanguage, setSelectedLanguage] = useState(initialLang || 'fr')
  const selectedLanguageRef = useRef(selectedLanguage)
  selectedLanguageRef.current = selectedLanguage
  const {refetchData} = useQueryResponse()
  const [PageForEdit] = useState<Page & {translations: PageTranslation[]}>({
    ...pageData,
    translations: ensureTranslationForLanguage(pageData.translations, initialLang || 'fr'),
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }
  const formik = useFormik({
    initialValues: PageForEdit,
    validationSchema: editPageSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find(
          (t) => normalizeLang(t.language) === normalizeLang(selectedLanguageRef.current)
        )

        if (!selectedTranslation || !String(selectedTranslation.title || '').trim()) {
          toast.error(intl.formatMessage({id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE'}))
          setSubmitting(false)
          return
        }
        const translationPayload: Record<string, unknown> = {
          language: normalizeLang(selectedTranslation.language) || selectedLanguageRef.current,
          title: selectedTranslation.title,
          metaTitle: selectedTranslation.metaTitle || '',
          metaDescription: selectedTranslation.metaDescription || '',
          metaKeywords: Array.isArray(selectedTranslation.metaKeywords)
            ? selectedTranslation.metaKeywords
            : [],
          openGraphType: selectedTranslation.openGraphType || '',
          openGraphTitle: selectedTranslation.openGraphTitle || '',
          openGraphDescription: selectedTranslation.openGraphDescription || '',
          openGraphImage: selectedTranslation.openGraphImage || '',
          twitterTitle: selectedTranslation.twitterTitle || '',
          twitterDescription: selectedTranslation.twitterDescription || '',
          twitterImage: selectedTranslation.twitterImage || '',
        }
        if (selectedTranslation._id) {
          translationPayload._id = selectedTranslation._id
        }
        const payload: Record<string, unknown> = {
          translations: [translationPayload],
        }

        if (values.slug) {
          payload.slug = values.slug
        }

        try {
          const response = await updateOne(payload, `/pages/${itemIdForUpdate}`)
          const code = response?.payload?.code
          if (isSuccessCode(code) || (response && code == null)) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.PAGE_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
            cancel(true)
            refetchData()
          } else if (code === 4401) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          } else if (code === 9100) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          } else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          }
        } catch (ex: any) {
          const apiMsg = ex?.response?.data?.message
          toast.error(
            Array.isArray(apiMsg)
              ? apiMsg.join(', ')
              : typeof apiMsg === 'string' && apiMsg.trim()
                ? apiMsg
                : intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
                  }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        } finally {
          setSubmitting(false)
        }
      } else {
        const payload = {
          slug: values.slug,
          title: values.translations[0].title,
          translations: values.translations.map((translation) => ({
            language: translation.language || 'en',
            title: translation.title,
            metaTitle: translation.metaTitle,
            metaDescription: translation.metaDescription,
            metaKeywords: translation.metaKeywords,
            openGraphType: translation.openGraphType,
            openGraphTitle: translation.openGraphTitle,
            openGraphDescription: translation.openGraphDescription,
            openGraphImage: translation.openGraphImage,
            twitterTitle: translation.twitterTitle,
            twitterDescription: translation.twitterDescription,
            twitterImage: translation.twitterImage,
          })),
        }
        try {
          await createOne(payload, '/pages').then((response) => {
            if (response?.payload?.code === 2021) {
              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.PAGES_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
              cancel(true)
            } else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            }
          })
        } catch (ex: any) {
          const apiMsg = ex?.response?.data?.message
          toast.error(
            Array.isArray(apiMsg)
              ? apiMsg.join(', ')
              : typeof apiMsg === 'string' && apiMsg.trim()
                ? apiMsg
                : intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE',
                  }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  useEffect(() => {
    void getAll({}, '/languages').then((data) => {
      const codes = extractLanguagesList(data)
        .map((code) => normalizeLang(code))
        .filter((code) => /^[a-z]{2}$/.test(code))
      if (!codes.length) return
      setLanguages(
        codes.map((code) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }))
      )
    })
  }, [LANGUAGE_NAMES])

  useEffect(() => {
    const lang = normalizeLang(selectedLanguage) || 'fr'
    if (lang !== selectedLanguage) {
      setSelectedLanguage(lang)
      return
    }
    const hasLocale = formik.values.translations.some((t) => normalizeLang(t.language) === lang)
    if (!hasLocale) {
      void formik.setFieldValue(
        'translations',
        ensureTranslationForLanguage(formik.values.translations, lang)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage])

  const setTranslationField = (field: keyof PageTranslation, value: unknown) => {
    const lang = normalizeLang(selectedLanguage) || 'fr'
    const translations = ensureTranslationForLanguage(formik.values.translations, lang)
    let index = translations.findIndex((t) => normalizeLang(t.language) === lang)
    if (index < 0) {
      translations.push(emptyTranslation(lang))
      index = translations.length - 1
    }
    const next = translations.map((translation, i) =>
      i === index ? {...translation, language: lang, [field]: value} : translation
    )
    void formik.setFieldValue('translations', next)
    void formik.setFieldTouched(`translations[${index}].${String(field)}`, true)
  }

  const selectedTranslation =
    formik.values.translations.find(
      (t) => normalizeLang(t.language) === normalizeLang(selectedLanguage)
    ) || emptyTranslation(selectedLanguage)
  const translationIndex = Math.max(
    0,
    formik.values.translations.findIndex(
      (t) => normalizeLang(t.language) === normalizeLang(selectedLanguage)
    )
  )
  const translationErrors = (formik.errors.translations?.[translationIndex] || {}) as Record<
    string,
    string
  >
  const showFieldError = (field: string) =>
    Boolean(formik.submitCount > 0 && translationErrors[field])
  const languageOptions =
    languages.length > 0
      ? languages
      : [
          {code: 'fr', name: LANGUAGE_NAMES.fr},
          {code: 'en', name: LANGUAGE_NAMES.en},
          {code: 'de', name: LANGUAGE_NAMES.de},
        ] 


  return (
    <>
      <form id='kt_modal_add_page_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column' style={{maxHeight: '55vh', overflowY: 'auto'}}>
          <div className='fv-row mb-5'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.LANGUAGE'})}
            </label>
            <select
              id='page-edit-language'
              name='pageLanguage'
              onChange={(e) => setSelectedLanguage(normalizeLang(e.target.value) || 'fr')}
              value={normalizeLang(selectedLanguage) || 'fr'}
              className='form-select'
            >
              {languageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-5 row'>
            {/* begin::Label */}
            <div className='col-12 col-md-4 mb-3 mb-md-0'>
              <label className='required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.NAME'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.NAME'})}
                value={selectedTranslation?.title || ''}
                onChange={(e) => setTranslationField('title', e.target.value)}
                onBlur={formik.handleBlur}
                type='text'
                name='title'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.title &&
                      (formik.errors.translations?.[translationIndex] as {title?: string})?.title,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.title &&
                      !(formik.errors.translations?.[translationIndex] as {title?: string})?.title,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
              {formik.touched.translations?.[translationIndex]?.title && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>
                      {(formik.errors.translations?.[translationIndex] as {title?: string})?.title || ''}
                    </span>
                  </div>
                </div>
              )}
              {/* end::Input */}
            </div>
            <div className='col-12 col-md-4'>
              <label className='required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.SLUG'})}
              </label>
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.SLUG'})}
                value={String(formik.values.slug) || ''}
                onChange={(e) => {
                  formik.setFieldValue('slug', e.target.value);
                  formik.setFieldTouched('slug', true);
                }}
                onBlur={formik.handleBlur}
                type='text'
                name='slug'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid': formik.touched.slug &&
                      (formik.errors.slug as {slug?: string})?.slug,
                  },
                  {
                    'is-valid': formik.touched.slug &&
                      !(formik.errors.slug as {slug?: string})?.slug,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
              {formik.touched.slug && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>
                      {formik.errors.slug ? String(formik.errors.slug) : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className='col-12 col-md-4'>
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
              </label>
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
                value={selectedTranslation?.metaTitle || ''}
                onChange={(e) => setTranslationField('metaTitle', e.target.value)}
                type='text'
                name='metaTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.metaTitle &&
                      (formik.errors.translations?.[translationIndex] as {metaTitle?: string})?.metaTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.metaTitle &&
                      !(formik.errors.translations?.[translationIndex] as {metaTitle?: string})?.metaTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
          </div>
          {/* end::Input group */}

          {/* Nouvelle div pour metaDescription */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
              value={selectedTranslation?.metaDescription || ''}
              onChange={(e) => setTranslationField('metaDescription', e.target.value)}
              name='metaDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[translationIndex]?.metaDescription &&
                    (formik.errors.translations?.[translationIndex] as {metaDescription?: string})
                      ?.metaDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[translationIndex]?.metaDescription &&
                    !(formik.errors.translations?.[translationIndex] as {metaDescription?: string})
                      ?.metaDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting}
            />
            {showFieldError('metaDescription') && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{translationErrors.metaDescription}</span>
                </div>
              </div>
            )}
          </div>

          <div className='fv-row mb-5 row'>
            <div className='col-12 col-md-4'>
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TYPE'})}
              </label>
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TYPE'})}
                value={selectedTranslation?.openGraphType || ''}
                onChange={(e) => setTranslationField('openGraphType', e.target.value)}
                type='text'
                name='openGraphType'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.openGraphType &&
                      (formik.errors.translations?.[translationIndex] as {openGraphType?: string})?.openGraphType,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.openGraphType &&
                      !(formik.errors.translations?.[translationIndex] as {openGraphType?: string})?.openGraphType,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
            <div className='col-12 col-md-4'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_TITLE'})}
                value={selectedTranslation?.openGraphTitle || ''}
                onChange={(e) => setTranslationField('openGraphTitle', e.target.value)}
                type='text'
                name='openGraphTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.openGraphTitle &&
                      (formik.errors.translations?.[translationIndex] as {openGraphTitle?: string})
                        ?.openGraphTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.openGraphTitle &&
                      !(formik.errors.translations?.[translationIndex] as {openGraphTitle?: string})
                        ?.openGraphTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
            <div className='col-12 col-md-4'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_IMAGE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_IMAGE'})}
                value={selectedTranslation?.openGraphImage || ''}
                onChange={(e) => setTranslationField('openGraphImage', e.target.value)}
                type='text'
                name='openGraphImage'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.openGraphImage &&
                      (formik.errors.translations?.[translationIndex] as {openGraphImage?: string})
                        ?.openGraphImage,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.openGraphImage &&
                      !(formik.errors.translations?.[translationIndex] as {openGraphImage?: string})
                        ?.openGraphImage,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
          </div>

          {/* Nouvelle div pour Open Graph Description */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION'})}
              value={selectedTranslation?.openGraphDescription || ''}
              onChange={(e) => setTranslationField('openGraphDescription', e.target.value)}
              name='openGraphDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[translationIndex]?.openGraphDescription &&
                    (formik.errors.translations?.[translationIndex] as {openGraphDescription?: string})
                      ?.openGraphDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[translationIndex]?.openGraphDescription &&
                    !(formik.errors.translations?.[translationIndex] as {openGraphDescription?: string})
                      ?.openGraphDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting}
            />
          </div>

          <div className='fv-row mb-5 row'>
            <div className='col-12 col-md-6 mb-3 mb-md-0'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.TWITTER_TITLE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_TITLE'})}
                value={selectedTranslation?.twitterTitle || ''}
                onChange={(e) => setTranslationField('twitterTitle', e.target.value)}
                type='text'
                name='twitterTitle'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.twitterTitle &&
                      (formik.errors.translations?.[translationIndex] as {twitterTitle?: string})?.twitterTitle,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.twitterTitle &&
                      !(formik.errors.translations?.[translationIndex] as {twitterTitle?: string})?.twitterTitle,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
            <div className='col-12 col-md-6'>
              {/* begin::Label */}
              <label className='fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'COLUMNS.TWITTER_IMAGE'})}
              </label>
              {/* end::Label */}

              {/* begin::Input */}
              <input
                placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_IMAGE'})}
                value={selectedTranslation?.twitterImage || ''}
                onChange={(e) => setTranslationField('twitterImage', e.target.value)}
                type='text'
                name='twitterImage'
                className={clsx(
                  'form-control mb-3 mb-lg-0',
                  {
                    'is-invalid':
                      formik.touched.translations?.[translationIndex]?.twitterImage &&
                      (formik.errors.translations?.[translationIndex] as {twitterImage?: string})?.twitterImage,
                  },
                  {
                    'is-valid':
                      formik.touched.translations?.[translationIndex]?.twitterImage &&
                      !(formik.errors.translations?.[translationIndex] as {twitterImage?: string})?.twitterImage,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting}
              />
            </div>
          </div>

          {/* Nouvelle div pour Twitter Description */}
          <div className='fv-row mb-5'>
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'COLUMNS.TWITTER_DESCRIPTION'})}
            </label>
            <textarea
              placeholder={intl.formatMessage({id: 'COLUMNS.TWITTER_DESCRIPTION'})}
              value={selectedTranslation?.twitterDescription || ''}
              onChange={(e) => setTranslationField('twitterDescription', e.target.value)}
              name='twitterDescription'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[translationIndex]?.twitterDescription &&
                    (formik.errors.translations?.[translationIndex] as {twitterDescription?: string})
                      ?.twitterDescription,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[translationIndex]?.twitterDescription &&
                    !(formik.errors.translations?.[translationIndex] as {twitterDescription?: string})
                      ?.twitterDescription,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting}
            />
          </div>

          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6'>
              {intl.formatMessage({ id: 'COLUMNS.SEO_KEYWORDS' })}
            </label>
            <TagsComponent
              key={selectedLanguage + (selectedTranslation?.metaKeywords?.join() || '')}
              initialValues={selectedTranslation?.metaKeywords || []}
              onValuesChange={(updatedMetaKeywords) => {
                setTranslationField('metaKeywords', updatedMetaKeywords)
              }}
              type='metaKeywords'
              selectedLanguage={selectedLanguage}
            />
          </div>
        </div>
        {/* end::Scroll */}

        {formik.submitCount > 0 && !formik.isValid && (
          <div className='alert alert-danger py-3 px-4 mt-6 mb-0 text-start'>
            {formik.errors.slug
              ? String(formik.errors.slug)
              : translationErrors.title ||
                translationErrors.metaDescription ||
                translationErrors.twitterDescription ||
                translationErrors.metaTitle ||
                intl.formatMessage({id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_PAGE'})}
          </div>
        )}

        {/* begin::Actions */}
        <div className='text-center pt-6'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-Pages-modal-action='cancel'
            disabled={formik.isSubmitting}
          >
            {intl.formatMessage({id: 'HEADER.RESET'})}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
            disabled={formik.isSubmitting}
          >
            <span className='indicator-label'> {intl.formatMessage({id: 'HEADER.SAVE'})}</span>
            {formik.isSubmitting && (
              <span className='indicator-progress'>
                {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {formik.isSubmitting && <DataLoading />}
    </>
  )
}

export {PageEditModalForm}
